import type { Schema } from '@/shared/api/contract';

/** Straight from the contract: cubas.domain.schemas.stats. */
export type TriageStats = Schema<'TriageStats'>;
export type TriageAccuracy = Schema<'TriageAccuracy'>;
export type TriageCost = Schema<'TriageCost'>;
export type CorrectionPair = Schema<'CorrectionPair'>;
export type ConfusionCell = Schema<'ConfusionCell'>;
export type TriageLatency = Schema<'TriageLatency'>;

/** The periods the screen offers. `custom` is two dates typed by hand. */
export type Period = '7' | '30' | 'custom';

/**
 * The `[from, to)` window for a period, as the ISO instants the API takes.
 *
 * `to` is **now**, not midnight: the back defaults to the same, and a review at 11:00
 * wants the corrections made at 10:50. The custom range is two calendar days, inclusive
 * of both, read in the browser's zone: the day Víctor typed is the day he meant.
 */
export function windowFor(
  period: Period,
  custom: { from: string; to: string },
  now: Date = new Date(),
): { from?: string; to?: string } | null {
  if (period === 'custom') {
    if (!custom.from || !custom.to) return null;
    const from = new Date(`${custom.from}T00:00:00`);
    const to = new Date(`${custom.to}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return null;
    to.setDate(to.getDate() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const from = new Date(now);
  from.setDate(from.getDate() - Number(period));
  return { from: from.toISOString(), to: now.toISOString() };
}

/** Seconds as a person reads them: `12 s`, `1 min 30 s`, `2 h 05 min`. */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ${String(Math.round(seconds % 60)).padStart(2, '0')} s`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
}

/**
 * The cells of the confusion matrix as a grid: every proposed and every final
 * destination that appears, in a stable order, and the count for each pair (0 when the
 * back sent no cell). The back sends it sparse so this is what draws the table.
 */
export function confusionGrid(cells: ConfusionCell[]): {
  proposed: string[];
  finals: string[];
  count: (proposed: string, final: string) => number;
} {
  const proposed = [...new Set(cells.map((cell) => cell.proposed))].sort();
  const finals = [...new Set(cells.map((cell) => cell.final))].sort();
  const index = new Map(cells.map((cell) => [`${cell.proposed}\u0000${cell.final}`, cell.count]));
  return {
    proposed,
    finals,
    count: (p, f) => index.get(`${p}\u0000${f}`) ?? 0,
  };
}

/**
 * Accuracy as a string, with the sample it was computed from.
 *
 * `accuracy_pct` is **null** when nobody has reviewed anything, and null is not zero: the
 * back is explicit about it and so is this. Printing 0 % for an empty sample would tell
 * Víctor the system is failing when what is happening is that nobody has looked yet, and
 * that is the difference between "fix the classifier" and "spend fifteen minutes in the
 * inbox".
 */
export function accuracyText(row: TriageAccuracy): string {
  if (row.accuracy_pct == null) {
    return row.proposed === 0 ? 'sin datos' : 'sin revisar';
  }
  return `${PCT.format(row.accuracy_pct)} %`;
}

const PCT = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * How the accuracy should read: green from the plan's own bar (85 %, the eval gate),
 * amber above the contractual floor (60 %), red below it, neutral with no sample.
 */
export function accuracyTone(row: TriageAccuracy): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (row.accuracy_pct == null) return 'neutral';
  if (row.accuracy_pct >= 85) return 'ok';
  if (row.accuracy_pct >= 60) return 'warn';
  return 'danger';
}

const eur = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 4,
});

/** The API sends money as a decimal string, so it never goes through a float. */
export function formatEur(value: string): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? eur.format(amount) : value;
}
